import * as React from 'react'
import { Col, FormControl, FormGroup, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Variation } from '../articlesContainer'

import './VariationItem.scss'

interface VariationItemProps {
	name: string
	type: string
	componentClass?: React.ReactType
	placeholder?: string
	value: string
	onChange: (e: React.FormEvent<FormControl & HTMLInputElement>) => void
	editable?: boolean
	textClass?: string
	textElement?: React.ReactType
	toggleEditable: (name?: string) => void
	maxLength?: number
	minLength?: number
	focus?: boolean
	textStyle?: React.CSSProperties
	suggestion: boolean
	showSuggestionDropdown?: boolean
	suggestions?: string[]
	suggestionIndex?: number
	closeSuggestionPopupExcept?: keyof Variation | undefined
	closeAllSuggestionsPopupsExcept?: (field: keyof Variation) => void
	readonly?: boolean
}

type ValidationState = 'min' | 'max' | 'ok'

function validationText(state: ValidationState) {
	if (state === 'ok') {
		return 'success'
	}

	return 'error'
}

function validate(text?: string, maxLength?: number, minLength?: number): ValidationState {
	if (minLength && (text ?? '').trim().length < minLength) return 'min'
	return maxLength && (text ?? '').trim().length > maxLength ? 'max' : 'ok'
}

interface Suggestion {
	content: string
	fromSuggestion: number
}

class VariationItem extends React.Component<
	VariationItemProps,
	{
		showSuggestions: boolean
	}
> {
	constructor(props: VariationItemProps) {
		super(props)
		this.state = {
			showSuggestions: false,
		}
	}

	componentDidUpdate(
		prevProps: Readonly<VariationItemProps>,
		prevState: Readonly<{ showSuggestionDropdown: boolean; showSuggestions: boolean }>,
	): void {
		if (
			// showing ai suggestion popup ...
			this.state.showSuggestions &&
			// ... but the parent component says "I don't want to see this type of field's ai popup" ...
			this.props.closeSuggestionPopupExcept !== this.props.name
		) {
			// ... so we close the popup to make the parent component happy
			this.setState({
				showSuggestions: false,
			})
		}
	}

	getSuggestions(): Suggestion[] {
		return (this.props.suggestions || [])
			.map((suggestion, index): Suggestion => ({ content: suggestion, fromSuggestion: index }))
			.filter((suggestion: Suggestion) => suggestion.fromSuggestion !== this.props.suggestionIndex)
	}

	toggleSuggestions() {
		// we're not showing ai suggestions, so we're about to show it - close all other
		// ai suggestions popups belonging to other VariationItems in case they're open
		if (!this.state.showSuggestions) {
			this.props.closeAllSuggestionsPopupsExcept &&
				this.props.closeAllSuggestionsPopupsExcept(this.props.name as keyof Variation)
		}
		this.setState({ showSuggestions: !this.state.showSuggestions })
	}

	pickSuggestion(index: number) {
		// ouch, very very ugly! TODO: write this properly
		this.props.onChange(({
			currentTarget: { value: this.props!.suggestions![index] },
		} as unknown) as React.FormEvent<FormControl & HTMLInputElement>)

		this.toggleSuggestions()
	}

	public render() {
		const validationState = validate(this.props.value, this.props.maxLength, this.props.minLength)
		return this.props.editable ? (
			<FormGroup
				className="VariationItem"
				controlId={this.props.name}
				validationState={validationText(validationState)}
			>
				<Col md={12}>
					<OverlayTrigger
						trigger={['focus']}
						placement="bottom"
						overlay={
							<Tooltip id={this.props.name}>
								{this.props.value?.length} / {this.props.maxLength} characters used
								{this.props.minLength && (
									<>
										<br />
										(Minimum {this.props.minLength} characters)
									</>
								)}
							</Tooltip>
						}
					>
						<div
							className={
								this.props.showSuggestionDropdown
									? 'VariationItemFormControlSuggestions' +
									  (this.state.showSuggestions ? ' expanded' : '')
									: ''
							}
						>
							<FormControl
								type={this.props.type}
								componentClass={this.props.componentClass}
								placeholder={this.props.placeholder}
								value={this.props.value}
								onChange={this.props.onChange}
								onKeyPress={(event) => {
									if (event.key === 'Enter' && this.props.toggleEditable) {
										this.props.toggleEditable()
									}
								}}
								autoFocus={this.props.focus}
								maxLength={this.props.maxLength}
								rows={this.props.maxLength && Math.ceil(this.props.maxLength / 34)}
								data-test={this.props.name}
							/>
							{this.state.showSuggestions && (
								<div
									onClick={this.toggleSuggestions.bind(this)}
									className="VariationItemFormControlSuggestionsTool VariationItemFormControlSuggestionsNumber"
								>
									<div>{(this.props.suggestionIndex || 0) + 1}</div>
								</div>
							)}
							{this.props.showSuggestionDropdown && (
								<div
									onClick={this.toggleSuggestions.bind(this)}
									className="VariationItemFormControlSuggestionsTool VariationItemFormControlSuggestionsToggler"
								>
									V
								</div>
							)}
						</div>
					</OverlayTrigger>
					{this.state.showSuggestions && (
						<div className="VariationItemFormControlSuggestionsContainerParent">
							<div className="VariationItemFormControlSuggestionsContainer">
								{this.getSuggestions().map((suggestion) =>
									suggestion.content === '' ? (
										<React.Fragment key={suggestion.fromSuggestion}></React.Fragment>
									) : (
										<div
											className="VariationItemFormControlSuggestionsContainerItemContainer"
											key={suggestion.fromSuggestion}
										>
											<div className="VariationItemFormControlSuggestionsContainerItemContainerNumber">
												{suggestion.fromSuggestion + 1}
											</div>
											<div className="VariationItemFormControlSuggestionsContainerItemContainerContent">
												<div
													className="suggestionText"
													onClick={this.pickSuggestion.bind(this, suggestion.fromSuggestion)}
												>
													{suggestion.content}
												</div>
											</div>
										</div>
									),
								)}
							</div>
						</div>
					)}
				</Col>
			</FormGroup>
		) : (
			<FormGroup
				className="VariationItem"
				controlId={this.props.name}
				onClick={() => this.props.toggleEditable(this.props.name)}
			>
				<Col md={12}>
					{!this.props.readonly && !this.props.suggestion && validationState !== 'ok' ? (
						<OverlayTrigger
							trigger={['hover']}
							placement="top"
							overlay={
								<Tooltip id={this.props.name}>
									{validationState === 'max'
										? 'The text in this field is too long! Click here to edit'
										: `The text in this field is too short! Click here to edit`}
								</Tooltip>
							}
						>
							<div
								data-testid={`variation-overlay-${this.props.name}`}
								className="bg-danger"
								style={{
									borderWidth: '2px',
									borderStyle: 'solid',
									borderColor: 'red',
									padding: '10px',
									display: 'flex',
								}}
							>
								<div
									style={{
										marginTop: 0,
										fontSize: '1.5em',
										paddingRight: '10px',
									}}
								>
									<i className="fa fa-exclamation-triangle text-danger" />
								</div>
								<div>{this.props.value ? this.props.value : this.props.placeholder}</div>
							</div>
						</OverlayTrigger>
					) : (
						<p className={this.props.textClass}>
							{this.props.textElement ? (
								<this.props.textElement className={this.props.textClass} style={this.props.textStyle}>
									{this.props.value ? this.props.value : this.props.placeholder}
								</this.props.textElement>
							) : this.props.value ? (
								this.props.value
							) : (
								this.props.placeholder
							)}
						</p>
					)}
				</Col>
			</FormGroup>
		)
	}
}

export default VariationItem
